import { z } from 'zod';
import { textBlockSchema, toolUseBlockSchema, toolResultBlockSchema } from './schema';

/**
 * Internal (native-chat) conversation vocabularies and content-part union.
 *
 * These are the database-authoring sources for the participant-first
 * conversation spine. They are deliberately independent of the frozen Pangea
 * contract in `./schema.ts`: the three original block shapes are composed into
 * the part union unchanged (stored rows keep parsing), but the frozen enums
 * are never widened and never imported as DB CHECK sources again — Pangea
 * compatibility is owed only at the egress adapter.
 */

// ─── Conversation axes (ADR-056 Rule 8 governed sources) ─────────────────────
//
// Purpose (`classification`), topology (`kind`), and lifecycle are orthogonal:
// a clinical conversation may contain clinicians and AI at once, and source
// modality (voice vs typed) is per-message provenance, never a conversation
// type. Each array backs both the Postgres CHECK and the Zod enum.

export const CONVERSATION_KIND = ['direct', 'group'] as const;
export const conversationKindSchema = z.enum(CONVERSATION_KIND);
export type ConversationKind = z.infer<typeof conversationKindSchema>;

export const CONVERSATION_CLASSIFICATION = ['agent', 'clinical'] as const;
export const conversationClassificationSchema = z.enum(CONVERSATION_CLASSIFICATION);
export type ConversationClassification = z.infer<typeof conversationClassificationSchema>;

export const CONVERSATION_LIFECYCLE = ['active', 'closed', 'archived'] as const;
export const conversationLifecycleSchema = z.enum(CONVERSATION_LIFECYCLE);
export type ConversationLifecycle = z.infer<typeof conversationLifecycleSchema>;

// ─── Source boundary ─────────────────────────────────────────────────────────
//
// Provider/session state is deliberately not part of the conversation row.
// A conversation may have zero or more source records, while native chat has
// none. `egressPolicy` is positive evidence for a cross-product export; null
// means no egress, never "anything that is not clinical".

export const CONVERSATION_SOURCE_TYPE = ['voice_agent', 'legacy_chat'] as const;
export const conversationSourceTypeSchema = z.enum(CONVERSATION_SOURCE_TYPE);
export type ConversationSourceType = z.infer<typeof conversationSourceTypeSchema>;

// The provider a voice-agent transcript is ingested under. Writer (the
// transcript adapter) and readers that look a voice session's transcript up by
// its provider conversation id share this one value, so the lookup always hits
// the (provider, external_conversation_id) unique key.
export const VOICE_AGENT_SOURCE_PROVIDER = 'elevenlabs';

// Source types whose rows are imported history rather than live traffic. A
// message or participant whose `origin_source_id` names one of these sources is
// "imported": the database lets it sit below the native timeline, carry the
// care-team role and be authored by a closed historical participant. The same
// set is written once in the `conversation_source_is_import` SQL helper.
export const CONVERSATION_IMPORT_SOURCE_TYPE = [
  'legacy_chat',
] as const satisfies readonly ConversationSourceType[];
export const conversationImportSourceTypeSchema = z.enum(CONVERSATION_IMPORT_SOURCE_TYPE);
export type ConversationImportSourceType = z.infer<typeof conversationImportSourceTypeSchema>;

export const CONVERSATION_SOURCE_STATUS = ['processing', 'completed', 'failed'] as const;
export const conversationSourceStatusSchema = z.enum(CONVERSATION_SOURCE_STATUS);
export type ConversationSourceStatus = z.infer<typeof conversationSourceStatusSchema>;

export const PANGEA_EGRESS_POLICY = ['pangea_ingestion_v1'] as const;
export const pangeaEgressPolicySchema = z.enum(PANGEA_EGRESS_POLICY);
export type PangeaEgressPolicy = z.infer<typeof pangeaEgressPolicySchema>;

// ─── Participants ─────────────────────────────────────────────────────────────
//
// Every actor in a conversation is a `conversation_participants` row with
// exactly one identity reference matching its type. Membership is identity,
// not authorization (ADR-087): whether an internal user may act still resolves
// through assignment + permission policy.

export const PARTICIPANT_TYPE = ['member', 'internal_user', 'agent', 'system'] as const;
export const participantTypeSchema = z.enum(PARTICIPANT_TYPE);
export type ParticipantType = z.infer<typeof participantTypeSchema>;

// ─── Message roles ────────────────────────────────────────────────────────────
//
// The internal actor-class vocabulary. `role` is derived from the sender
// participant's type at write time (member → user, internal_user → care_team,
// agent → agent, system → system) — it is never accepted as caller input, and
// `sender_participant_id` remains the attribution source of truth.

export const MESSAGE_ROLE = ['user', 'agent', 'care_team', 'system'] as const;

/**
 * Provenance of a care-team message, additive to the sender (ADR-098 Rule 3:
 * the sender stays the clinician's participant). `system_on_behalf` is written
 * only by the trusted outreach workflow against an approved outreach row;
 * `human_edited` marks generated copy a reviewer changed before send. The
 * member-facing surface renders all three identically in slice 1.
 */
export const MESSAGE_AUTHORSHIP = ['human', 'system_on_behalf', 'human_edited'] as const;
export const messageAuthorshipSchema = z.enum(MESSAGE_AUTHORSHIP);
export type MessageAuthorship = z.infer<typeof messageAuthorshipSchema>;
export const messageRoleSchema = z.enum(MESSAGE_ROLE);
export type MessageRole = z.infer<typeof messageRoleSchema>;

/** Canonical participant-role map mirrored by the database authorship trigger. */
export const MESSAGE_ROLE_BY_PARTICIPANT_TYPE = {
  member: 'user',
  internal_user: 'care_team',
  agent: 'agent',
  system: 'system',
} as const satisfies Record<ParticipantType, MessageRole>;

// ─── Message read status ──────────────────────────────────────────────────────
//
// Whether the receiving member's durable read watermark has passed a message.
// Derived only from `conversation_read_states.last_read_position` — never from
// realtime presence — and carried on the received-message domain event so a
// downstream system can update its receipt record in place.

export const MESSAGE_READ_STATUS = ['unread', 'read'] as const;
export const messageReadStatusSchema = z.enum(MESSAGE_READ_STATUS);
export type MessageReadStatus = z.infer<typeof messageReadStatusSchema>;

// ─── Content parts ────────────────────────────────────────────────────────────
//
// The internal discriminated union stored in `messages.content`. The three
// original block shapes are reused byte-for-byte so every existing row stays
// valid; the new parts are closed objects so junk shapes surface as writer
// bugs instead of tolerated data.

/**
 * A renderable GenUI document (ADR-046 `root` + `elements`), optionally with
 * initial state. `catalogVersion` anchors the part to the catalog it was
 * authored against — messages are long-lived clinical records, and the anchor
 * lets a future renderer make an explicit compatibility decision. Structural
 * validation lives here; catalog validation happens at the write boundary.
 */
export const genuiPartSchema = z
  .object({
    type: z.literal('genui'),
    spec: z
      .object({
        root: z.string().min(1),
        elements: z.record(z.string(), z.unknown()),
      })
      .strict(),
    state: z.record(z.string(), z.unknown()).optional(),
    catalogVersion: z.string().min(1).optional(),
  })
  .strict();
export type GenuiPart = z.infer<typeof genuiPartSchema>;

/**
 * An opaque reference to a stored attachment record — never a raw bucket key
 * or URL (the uuid shape structurally rejects both). Upload lifecycle,
 * scanning, and resolution to bytes are write/read-boundary concerns.
 */
export const attachmentPartSchema = z
  .object({
    type: z.literal('attachment'),
    attachmentId: z.string().uuid(),
    mediaType: z.string().min(1),
    fileName: z.string().min(1).optional(),
    sizeBytes: z.number().int().nonnegative().optional(),
  })
  .strict();
export type AttachmentPart = z.infer<typeof attachmentPartSchema>;

// System events are governed, versioned variants — never a free-form `kind`
// string. New operational facts are added as new variants (or new versions of
// an existing variant), so consumers can switch exhaustively.
export const participantJoinedEventSchema = z
  .object({
    type: z.literal('system_event'),
    event: z.literal('participant_joined'),
    version: z.literal(1),
    participantId: z.string().uuid(),
  })
  .strict();

export const participantLeftEventSchema = z
  .object({
    type: z.literal('system_event'),
    event: z.literal('participant_left'),
    version: z.literal(1),
    participantId: z.string().uuid(),
  })
  .strict();

export const systemEventPartSchema = z.discriminatedUnion('event', [
  participantJoinedEventSchema,
  participantLeftEventSchema,
]);
export type SystemEventPart = z.infer<typeof systemEventPartSchema>;

/**
 * Human-authorable content. Tool traffic and membership events are trusted
 * execution facts, so member and care-team send APIs must never accept them.
 */
export const clientContentPartSchema = z.union([
  textBlockSchema,
  genuiPartSchema,
  attachmentPartSchema,
]);
export type ClientContentPart = z.infer<typeof clientContentPartSchema>;

/** Content an authenticated agent runtime or trusted source adapter may author. */
export const trustedAgentContentPartSchema = z.union([
  clientContentPartSchema,
  toolUseBlockSchema,
  toolResultBlockSchema,
]);
export type TrustedAgentContentPart = z.infer<typeof trustedAgentContentPartSchema>;

// Top-level union. `z.union` (not `discriminatedUnion`) because the
// system_event variants share `type` and discriminate on `event`.
export const contentPartSchema = z.union([trustedAgentContentPartSchema, systemEventPartSchema]);
export type ContentPart = z.infer<typeof contentPartSchema>;
